import React, { useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import { loadTemplate, fillTemplate, convertToPdf } from './docxTemplateEngine';
import ModalViewFile from '../../components/Modals/ModalViewFile';


/**
 * Pulsante riutilizzabile per stampare documenti DOCX come PDF.
 *
 * Props:
 * - templateKey: chiave del template in intestazioni (es. 'template_order_customer')
 * - loadData: async function che ritorna { templateData, filename, entity, id, fileDocument? }
 * - buttonLabel: testo del pulsante (default: 'Stampa')
 * - buttonVariant: variante Bootstrap (default: 'outline-secondary')
 * - buttonSize: dimensione (default: 'sm')
 * - className: classi CSS aggiuntive
 * - icon: icona FontAwesome (default: null)
 * - style: stile inline
 */
export default function PrintDocxButton({
    templateKey,
    loadData,
    buttonLabel = 'Stampa',
    buttonVariant = 'outline-secondary',
    buttonSize = 'sm',
    className = 'me-1',
    icon = null,
    style = null
}) {
    const [loading, setLoading] = useState(false);
    const [modalViewFileData, setModalViewFileData] = useState(null);

    async function handlePrint() {
        if (loading) return;
        setLoading(true);
        try {
            const result = await loadData();
            if (!result) {
                toast.error('Errore nel caricamento dei dati.');
                return;
            }

            // Se esiste già un file generato, mostralo direttamente
            if (result.fileDocument && result.fileDocument.hash) {
                setModalViewFileData({
                    filename: result.fileDocument.filename || (result.fileDocument.hash + '.' + (result.fileDocument.extension || 'pdf')),
                    extension: result.fileDocument.extension || 'pdf',
                    entity: result.entity,
                    hash: result.fileDocument.hash
                });
                return;
            }

            // Carica template DOCX
            const templateBuffer = await loadTemplate(templateKey);
            if (!templateBuffer) {
                toast.error('Template DOCX non trovato. Configurare il template in intestazioni.');
                return;
            }

            // Genera DOCX e converti in PDF
            const generatedDocx = fillTemplate(templateBuffer, result.templateData);
            const pdf = await convertToPdf(generatedDocx, result.filename, result.entity, result.id);
            if (pdf) {
                setModalViewFileData({
                    filename: pdf.filename || (result.filename + '.pdf'),
                    extension: 'pdf',
                    entity: pdf.entity || result.entity,
                    hash: pdf.hash
                });
            } else {
                toast.error('Errore nella conversione PDF. Verificare che il backend sia configurato.');
            }
        } catch (error) {
            console.error('Errore generazione template:', error);
            if (error.properties && error.properties.errors) {
                error.properties.errors.forEach((e, i) => {
                    console.error(`  Errore ${i + 1}:`, e.message, '| Tag:', e.properties?.id || e.properties?.xtag || 'N/A');
                });
            }
            toast.error('Errore nel template DOCX: ' + error.message);
        } finally {
            setLoading(false);
        }
    }

    return (
        <>
            <Button
                size={buttonSize}
                variant={buttonVariant}
                className={className}
                onClick={handlePrint}
                disabled={loading}
                style={style || undefined}
            >
                {loading ? (
                    <Spinner animation="border" size="sm" className={buttonLabel ? 'me-1' : ''} />
                ) : icon ? (
                    <FontAwesomeIcon icon={icon} className={buttonLabel ? 'me-1' : ''} />
                ) : null}
                {buttonLabel}
            </Button>
            {modalViewFileData && (
                <ModalViewFile
                    data={modalViewFileData}
                    closeModalViewFile={() => setModalViewFileData(null)}
                />
            )}
        </>
    );
}
